import { Maximize02 } from '@untitledui/icons';
import { useCallback, useState } from 'react';

import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { cx } from '@/utils/cx';

export interface ExpandableImageProps {
  src: string;
  alt: string;
  /** Higher-resolution source for the enlarged view. Defaults to `src`. */
  enlargedSrc?: string;
  /** Class applied to the thumbnail image. */
  className?: string;
}

const TRIGGER_CLASS =
  'group relative block w-full cursor-zoom-in overflow-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-brand-600)]';

const HINT_CLASS =
  'pointer-events-none absolute right-2 top-2 flex size-7 items-center justify-center rounded-md border border-[var(--color-border-secondary)] bg-[var(--color-bg-secondary)] text-[var(--color-text-secondary)] opacity-0 shadow-sm transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100';

const ENLARGED_IMG_CLASS =
  'mx-auto block max-h-[80vh] max-w-full object-contain';

/**
 * ExpandableImage — a thumbnail that opens a full-size lightbox (the base Modal)
 * on click. The trigger is keyboard-focusable and shows a maximize hint on hover;
 * the modal brings focus trapping, Esc-to-close, and an auto-rendered close button.
 */
export function ExpandableImage({
  src,
  alt,
  enlargedSrc,
  className,
}: ExpandableImageProps) {
  const [isOpen, setIsOpen] = useState(false);
  const open = useCallback(() => setIsOpen(true), []);

  return (
    <>
      <button
        type='button'
        onClick={open}
        aria-label={`Expand image: ${alt}`}
        aria-haspopup='dialog'
        className={TRIGGER_CLASS}
      >
        <img src={src} alt={alt} className={cx('block w-full', className)} />
        <span className={HINT_CLASS} aria-hidden='true'>
          <Maximize02 className='size-4' />
        </span>
      </button>
      <ModalOverlay isOpen={isOpen} onOpenChange={setIsOpen}>
        <Modal className='max-w-5xl'>
          <Dialog aria-label={alt}>
            <div className='pb-6'>
              <img
                src={enlargedSrc ?? src}
                alt={alt}
                className={ENLARGED_IMG_CLASS}
              />
            </div>
          </Dialog>
        </Modal>
      </ModalOverlay>
    </>
  );
}
